/** Canvas ↔ Overview bidirectional navigation context
 * (VISTOERA_TENANT_OVERVIEW_DASHBOARD_LAYOUT_AND_CANVAS_RETURN_CONTEXT_P1_V1,
 * VISTOERA_STAR_CRYSTAL_3D_V_GLOBAL_MARK_CANVAS_RETURN_AND_GLOBAL_SCROLLBAR_P0_V1,
 * VistoeraTASK-00552).
 *
 * Persists which Content Project the user was editing so:
 * - Canvas "我的 Vistoera" → Workspace Overview (never Home Canvas / blank project)
 * - Overview "我的画布" → resume that same Content Project canvas
 *
 * Refresh-safe: ProductionCanvasPage re-syncs from URL `projectId` on mount. */

import { getExpectedSessionUser } from '../sessionIdentity'

export type CanvasSourceSurface = 'content-production' | 'overview' | 'direct-url' | 'home-redirect'

export interface CanvasNavigationContext {
  workspaceId: string
  contentProjectId: string
  sourceSurface: CanvasSourceSurface
  returnRoute: string
}

const STORAGE_PREFIX = 'vistoera.canvas.return.v1'

/** In-tab memory mirror of sessionStorage — survives SPA route swaps even if
 * sessionStorage write/read fails (private mode / quota). Never shared across
 * tabs or workspaces. VistoeraTASK-00552. */
const memoryByWorkspace = new Map<string, CanvasNavigationContext>()

/** VistoeraTASK-00576 - the return context holds a project id, so it is bound
 * to the signed-in user (`userId::workspaceId`), not just the workspace id:
 * account B in the same Workspace can never read account A's context. */
function scopeKey(workspaceId: string): string {
  return `${getExpectedSessionUser() ?? ''}::${workspaceId}`
}

function storageKey(workspaceId: string): string {
  return `${STORAGE_PREFIX}.${scopeKey(workspaceId)}`
}

function isValidContext(parsed: CanvasNavigationContext | null | undefined, workspaceId: string): parsed is CanvasNavigationContext {
  if (!parsed?.workspaceId || parsed.workspaceId !== workspaceId) return false
  if (!parsed.contentProjectId) return false
  return true
}

export function workspaceOverviewRoute(workspaceId: string): string {
  return `/workspace/${workspaceId}/overview`
}

/** Canonical canvas URL for an existing Content Project — never creates a project. */
export function contentProjectCanvasRoute(workspaceId: string, contentProjectId: string): string {
  return `/workspace/${workspaceId}/content-production/${contentProjectId}/canvas`
}

export function persistCanvasNavigationContext(ctx: CanvasNavigationContext): void {
  if (!isValidContext(ctx, ctx.workspaceId)) return
  memoryByWorkspace.set(scopeKey(ctx.workspaceId), ctx)
  try {
    sessionStorage.setItem(storageKey(ctx.workspaceId), JSON.stringify(ctx))
  } catch {
    // Private mode / quota — in-memory mirror still serves this tab.
  }
}

export function readCanvasNavigationContext(workspaceId: string): CanvasNavigationContext | null {
  const fromMemory = memoryByWorkspace.get(scopeKey(workspaceId))
  if (isValidContext(fromMemory, workspaceId)) return fromMemory
  try {
    const raw = sessionStorage.getItem(storageKey(workspaceId))
    if (!raw) return null
    const parsed = JSON.parse(raw) as CanvasNavigationContext
    if (!isValidContext(parsed, workspaceId)) return null
    memoryByWorkspace.set(scopeKey(workspaceId), parsed)
    return parsed
  } catch {
    return null
  }
}

/** VistoeraTASK-00577 — projects this tab has seen as NOT owned by the signed-in
 * user (a teammate's shared / member project, read-only). They may be VIEWED via
 * their explicit entries but must never become the Canvas Return Context, so the
 * return context cannot later be mistaken for "my canvas". Keyed
 * `userId::workspaceId::projectId`. */
const notOwnProjects = new Set<string>()

function notOwnKey(workspaceId: string, contentProjectId: string): string {
  return `${scopeKey(workspaceId)}::${contentProjectId}`
}

/** Record whether the project currently on screen is owned by the signed-in user. */
export function setCanvasProjectOwnership(workspaceId: string, contentProjectId: string, own: boolean): void {
  if (!workspaceId || !contentProjectId) return
  const key = notOwnKey(workspaceId, contentProjectId)
  if (own) {
    notOwnProjects.delete(key)
    return
  }
  notOwnProjects.add(key)
  // Drop an already-stored context that points at this foreign project.
  if (readCanvasNavigationContext(workspaceId)?.contentProjectId === contentProjectId) {
    clearCanvasNavigationContext(workspaceId)
  }
}

/** Sync persisted context from the canonical Canvas URL relationship. */
export function syncCanvasNavigationContextFromProject(
  workspaceId: string,
  contentProjectId: string,
  sourceSurface: CanvasSourceSurface = 'direct-url',
): void {
  if (!workspaceId || !contentProjectId) return
  if (notOwnProjects.has(notOwnKey(workspaceId, contentProjectId))) return
  persistCanvasNavigationContext({
    workspaceId,
    contentProjectId,
    sourceSurface,
    returnRoute: workspaceOverviewRoute(workspaceId),
  })
}

/** Resolve where Canvas "我的 Vistoera" should land (Overview). */
export function resolveMyVistoeraReturnRoute(workspaceId: string, contentProjectId?: string | null): string {
  if (workspaceId && contentProjectId) {
    syncCanvasNavigationContextFromProject(workspaceId, contentProjectId, 'direct-url')
  }
  const stored = readCanvasNavigationContext(workspaceId)
  return stored?.returnRoute || workspaceOverviewRoute(workspaceId)
}

/**
 * Resolve where Overview brand V should land — resume last Content Project canvas.
 * Returns null when no persisted project exists (caller must NOT create a blank canvas).
 */
export function resolveCanvasResumeRoute(workspaceId: string): string | null {
  const stored = readCanvasNavigationContext(workspaceId)
  if (!stored?.contentProjectId) return null
  return contentProjectCanvasRoute(workspaceId, stored.contentProjectId)
}

/** Content projects list — safe landing when no canvas resume context exists. */
export function contentProjectsListRoute(workspaceId: string): string {
  return `/workspace/${workspaceId}/content-production`
}

/** Fullscreen Canvas entry — last-opened then Home Canvas (VistoeraTASK-00582/00573). */
export function workspaceHomeCanvasRoute(workspaceId: string): string {
  return `/workspace/${workspaceId}/home`
}

/** VistoeraTASK-00592 — account-menu「个人资料」lands on Overview settings/profile.
 * Legacy `/account/profile` redirects here; do not revive the standalone page. */
export function tenantProfileRoute(workspaceId: string): string {
  return `/workspace/${workspaceId}/overview?tab=settings&section=profile`
}

export function tenantPlanUsageRoute(workspaceId: string): string {
  return `/workspace/${workspaceId}/account/plan-usage`
}

/** Canonical explicit destinations for Canvas → Backoffice exit via the
 * Global Account Menu (TASK-00048 §5-§9). Avatar menu items take
 * precedence over last-backoffice-location memory. */
export type CanvasExitBackofficeDestination =
  | 'WORKSPACE_OVERVIEW'
  | 'PROFILE'
  | 'PLAN_USAGE'

export function resolveCanvasExitBackofficeRoute(
  workspaceId: string,
  destination: CanvasExitBackofficeDestination,
): string {
  switch (destination) {
    case 'WORKSPACE_OVERVIEW':
      return workspaceOverviewRoute(workspaceId)
    case 'PROFILE':
      return tenantProfileRoute(workspaceId)
    case 'PLAN_USAGE':
      return tenantPlanUsageRoute(workspaceId)
    default:
      return workspaceOverviewRoute(workspaceId)
  }
}

/** Ensure the active Canvas Return Context is fresh before any Canvas →
 * Backoffice exit (Avatar menu, CanvasBrandEntry, etc.). Viewport
 * persistence remains separate (`canvasViewportPersistence.ts`). */
export function ensureActiveCanvasReturnContext(
  workspaceId: string,
  contentProjectId: string,
  sourceSurface: CanvasSourceSurface = 'direct-url',
): void {
  if (!workspaceId || !contentProjectId) return
  syncCanvasNavigationContextFromProject(workspaceId, contentProjectId, sourceSurface)
}

export function clearCanvasNavigationContext(workspaceId: string): void {
  memoryByWorkspace.delete(scopeKey(workspaceId))
  try {
    sessionStorage.removeItem(storageKey(workspaceId))
  } catch {
    // Private mode / disabled storage.
  }
}

/** Account switch — drop every workspace's Canvas return context (it holds the
 * previous identity's project id). */
export function clearAllCanvasNavigationContexts(): void {
  memoryByWorkspace.clear()
  notOwnProjects.clear()
  try {
    const stale: string[] = []
    for (let i = 0; i < sessionStorage.length; i += 1) {
      const key = sessionStorage.key(i)
      if (key && key.startsWith(`${STORAGE_PREFIX}.`)) stale.push(key)
    }
    stale.forEach((key) => sessionStorage.removeItem(key))
  } catch {
    // Private mode / disabled storage.
  }
}

/** Node-test helper — wipe in-tab mirrors between cases. */
export function resetCanvasReturnContextMemoryForTests(): void {
  memoryByWorkspace.clear()
  notOwnProjects.clear()
}

export type VLogoDestination =
  | { kind: 'RETURN_TO_CANVAS'; route: string }
  /** No valid Canvas return context — enter Canvas via `/home`
   * (last-opened → Home Canvas). Never blocks Logo behind a toast
   * (VistoeraTASK-00573; supersedes TASK-00552 stay-put notice). */
  | { kind: 'ENTER_HOME_CANVAS'; route: string }

/** Unified Tenant Shell V-logo resolver (TASK-00048 §20-§23,
 * VistoeraTASK-00552 / VistoeraTASK-00573).
 *
 * When an active Canvas Return Context exists, the V logo ALWAYS resumes
 * that Canvas — regardless of whether the user is on Overview, Profile,
 * Plan Usage, Content Production, or any other backoffice route.
 *
 * Without context: enter Canvas via `/home` (resolves last-opened then
 * Home Canvas). Never toggles Overview ↔ Content Projects. Never toasts
 * "没有可返回的画布" as a navigation refusal.
 *
 * UNVALIDATED (sync, storage-only). VistoeraTASK-00577: the shipped V-logo uses
 * `resolveValidatedVLogoDestination`, which additionally proves the stored project
 * is OWNED by the signed-in user before resuming it. */
export function resolveVLogoDestination(workspaceId: string, _pathname?: string): VLogoDestination {
  const resume = resolveCanvasResumeRoute(workspaceId)
  if (resume) {
    return { kind: 'RETURN_TO_CANVAS', route: resume }
  }
  return { kind: 'ENTER_HOME_CANVAS', route: workspaceHomeCanvasRoute(workspaceId) }
}
